import Backbone from "backbone";
import _ from "underscore";
import DriveView from "../../../../DriveView";
import * as logger from "../../../../utils/logger";
import { generateChecklist } from "../../../framework/checklist";
import { dstCalculator } from "../../../../utils/date";
import Sync from "../../../../sync/sync";
import DutyStatusLog, { DRIVING_IN_EXEMPTION_CONTROL_STATUSES, HOS_STANDARD_STATUSES, IAddress } from "../../../../model/DutyStatusLog";
import { createElement } from "../../../../utils/vanilla";
import { CancelablePromise } from "../../../../utils/cancelablePromise";
import dayjs from "../../../../utils/dayjs";
import toastr from "../../../../utils/toastr";
import { SyncType } from "../../../../sync/sync.types";
import { PageLevel } from "../../../../store/mobile/types";
import { handleApi } from "../../../../utils/error";
import { FeatureFlagName } from "../../../../utils/featureFlagName";
import pageTemplate from "./logsReviewChanges.tmpl";
import logTemplate from "./log.reviewChanges.tmpl";
import SelectDialog from "../../../framework/selectDialog";
import {
    EditPcYmAction,
    fillExemptionGap,
    getExemptionPeriodLogs,
    getExemptionTypeIfValid,
    getLogsToRemove,
    getSdcLogWithUpdatedTime,
    IReviewChangesParams,
    validateExemptionTimeModification
} from "./editPcOrYmUtils";
import { convertMinutesToTimeString } from "../../../../utils/helpers";
import { getLogRecords, IAddressSearch, populateAddressesRedux } from "../../../../sync/common";
import { IApiError } from "../../../../utils/api.interfaces";
import type { THosStatus } from "hos-tools";
import { dispatcher, DispatcherEvents } from "../../../../utils/dispatcher";
import { createExemptionEditAnnotation, type IExemptionEditContext } from "./exemptionEditAnnotations";

/* eslint-disable @typescript-eslint/naming-convention */
interface IViewLog extends DutyStatusLog {
    _address: string;
    _date: string;
    _isEditable: boolean;
    className: string;
    _duration: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

export default class LogsReviewChangesView extends DriveView<Backbone.Model> {

    protected strings = [
        "Confirm",
        "Log updated",
        "Failed to save changes. Please try again.",
        "Saving in progress",
        "Accept",
        "Reject"
    ];

    private readonly sync: Sync;
    private readonly templatePage = _.template(pageTemplate);
    private readonly exemptionAction: EditPcYmAction;
    private readonly newSdcDateTime: Date | null;
    private readonly exemptionLog: DutyStatusLog;
    private readonly logsCollection: DutyStatusLog[] = [];
    private readonly editableLogSet: Set<string> = new Set();
    private readonly isRequestedEdit: boolean = false;
    private readonly workflow = false;
    private exemptionPeriodLogs: DutyStatusLog[] = [];
    private gapLogsCollection: DutyStatusLog[] = [];
    private loadLogsListPromise: CancelablePromise<void>;
    private isProcessingLogChanges = false;

    constructor(options) {
        super(options, {
            title: "Review changes",
            activeSection: "hos",
            pageLevel: options.pageLevel || PageLevel.TWO
        });

        this.app = options.app;
        this.sync = this.app.sync;
        this.workflow = options.workflow ?? false;

        try {
            const exemptionParams = options.state as IReviewChangesParams;

            if (exemptionParams) {
                this.isRequestedEdit = exemptionParams.isRequestedEdit ?? false;
                this.newSdcDateTime = exemptionParams.newSdcDateTime
                    ? new Date(exemptionParams.newSdcDateTime)
                    : null;
                this.exemptionAction = exemptionParams.exemptionAction ?? (this.newSdcDateTime ? EditPcYmAction.Shorten : EditPcYmAction.Remove);

                const logId = exemptionParams.logId;
                if (logId) {
                    this.logsCollection = this.app.dutyStatusLogService.getAllHOSStatusLogsIncludingSDC(this.isRequestedEdit, DRIVING_IN_EXEMPTION_CONTROL_STATUSES);
                    this.exemptionLog = this.logsCollection.find(log => log.id === logId) as DutyStatusLog;
                }
            }
        } catch (e) {
            logger.error(logger.Source.UI, "Error processing exemption parameters:", e);
        }
    }

    events() {
        return {
            "click .editStatus": e => this.editStatus(e),
            "click #confirmChanges": () => this.onConfirmLogChanges(),
            "click #acceptRequest": e => this.acceptRequest(e),
            "click #rejectRequest": e => this.rejectRequest(e)
        } as Backbone.EventsHash;
    }

    render() {
        this.exemptionPeriodLogs = getExemptionPeriodLogs(this.exemptionLog, this.logsCollection, this.app.dutyStatusLogService);

        if (!this.isEditValid()) {
            this.exitPage();
            return this;
        }

        if (!this.isRequestedEdit) {
            this.updateUIWithGapFillingLogs();
        } else {
            this.updateUIWithExistingGapFillingLogs();
        }
        this.renderImpl();
        return this;
    }

    remove() {
        this.cancelPendingPromises();
        super.remove();
        return this;
    }

    editStatus(e) {
        const target = e.currentTarget as HTMLElement;
        const logId = target.getAttribute("log-id");
        const log = this.gapLogsCollection.find(l => l.id === logId);

        if (!log) {
            return;
        }

        const statusDialog = new SelectDialog({
            translator: this.translator,
            title: "Choose status",
            selectCallback: statusObj => {
                let status = statusObj.id as THosStatus;
                if (status !== log.status) {
                    log.status = status;
                }
                this.renderImpl();
            },
            isUserNightMode: this.app.store.getState().data.nightMode
        });

        const formatRenderItems = (status: string) => ({
            id: status,
            name: status,
            isSelected: status === log.status
        });

        statusDialog.render(HOS_STANDARD_STATUSES.map(formatRenderItems));
    }

    private renderImpl() {
        this.loadLogsList().catch(handleApi);
    }

    private cancelPendingPromises() {
        this.loadLogsListPromise && this.loadLogsListPromise.cancel();
    }

    private readonly loadLogsList = () => {
        this.loadLogsListPromise = this.renderTemplate(this.populateDisplayInfo());
        return this.loadLogsListPromise;
    };

    private populateDisplayInfo(): IViewLog[] {
        if (!this.gapLogsCollection.length) {
            return [];
        }

        let getMinuteDuration = (log: DutyStatusLog, index: number): string => {
            const nextLog =
                index < this.gapLogsCollection.length - 1
                    ? this.gapLogsCollection[index + 1]
                    : this.exemptionPeriodLogs[this.exemptionPeriodLogs.length - 1];
            const duration = nextLog.getDate().getTime() - log.getDate().getTime();
            return convertMinutesToTimeString(duration > 0 ? duration / (1000 * 60) : 0);
        };

        return this.gapLogsCollection.map((log, index) => {
            return {
                ...log,
                _address: log._address || "",
                _date: dayjs(dstCalculator.toLocalDateTime(log.getDate())).format("lll"),
                _isEditable: this.editableLogSet.has(log.id),
                className: "log",
                _duration: getMinuteDuration(log, index)
            } as IViewLog;
        });
    }

    private updateUIWithExistingGapFillingLogs() {
        this.gapLogsCollection = this.exemptionPeriodLogs.filter(log => log.requestedAdd);
    }

    private updateUIWithGapFillingLogs() {
        this.gapLogsCollection = fillExemptionGap(this.exemptionPeriodLogs, this.exemptionLog, this.newSdcDateTime);

        this.gapLogsCollection.forEach(log => {
            if (log.status !== "D" && !this.editableLogSet.has(log.id)) {
                this.editableLogSet.add(log.id);
            }
        });
    }

    private onConfirmLogChanges() {
        if (this.isProcessingLogChanges) {
            return;
        }

        this.isProcessingLogChanges = true;
        const confirmButton = document.getElementById("confirmChanges") as HTMLButtonElement;
        if (confirmButton) {
            confirmButton.disabled = true;
            confirmButton.textContent = this.getString("Saving in progress");
        }
        this.processLogChanges();
    }

    private processLogChanges() {
        const editor = this.app.userService.getCurrentUser();
        if (!editor) {
            this.isProcessingLogChanges = false;
            toastr.error(this.getString("Failed to save changes. Please try again."));
            return;
        }
        const sdcLog = this.exemptionPeriodLogs[this.exemptionPeriodLogs.length - 1];
        const context: IExemptionEditContext = {
            status: this.exemptionLog.status as "PC" | "YM",
            start: this.exemptionLog.dateTime,
            end: sdcLog.dateTime,
            newEnd: this.newSdcDateTime?.toISOString() ?? null,
            editor,
            dateTime: new Date().toISOString()
        };
        const logsToAdd = this.gapLogsCollection.map((log: DutyStatusLog) => {
            const clonedLog = new DutyStatusLog(log);
            delete clonedLog.location;
            delete clonedLog._address;
            clonedLog.annotations = [createExemptionEditAnnotation(context, "replacement")];
            return clonedLog;
        });

        // Get the logs that should be removed
        const isIntPcRetentionEnabled = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_INTPC_RETENTION);
        const logsToDelete = getLogsToRemove(this.exemptionPeriodLogs, this.newSdcDateTime, isIntPcRetentionEnabled);
        const sdcLogToUpdate = getSdcLogWithUpdatedTime(sdcLog, this.newSdcDateTime);
        sdcLogToUpdate?.addAnnotation(createExemptionEditAnnotation(context, "source"));

        const addSourceAnnotation = (log: DutyStatusLog) => this.sync.annotations.addItem({
            ...createExemptionEditAnnotation(context, "source"),
            dutyStatusLog: { id: log.parentId ?? log.id }
        }, true);

        return this.app.hosUtils.checkNewStatusForDrivingInViolation(logsToAdd)
            .then(() => {
                return Promise.all([
                    ...logsToAdd.map(log => this.sync.dutyStatusLogs.addItem(log, true, true)),
                    addSourceAnnotation(this.exemptionLog),
                    ...(this.newSdcDateTime ? [] : [addSourceAnnotation(sdcLog)]),
                    ...logsToDelete.map(log => this.sync.dutyStatusLogs.removeItem(log, true)),
                    ...(sdcLogToUpdate !== null ? [this.sync.dutyStatusLogs.setItem(sdcLogToUpdate, true)] : [])
                ]);
            })
            .then(() => this.sync.synchronize(false, null, [SyncType.HOS]))
            .then(() => {
                toastr.info(this.getString("Log updated"));
                this.isProcessingLogChanges = false;
                this.exitPage();
            })
            .catch((e: IApiError) => {
                const confirmButton = document.getElementById("confirmChanges") as HTMLButtonElement;
                if (confirmButton) {
                    confirmButton.disabled = false;
                    confirmButton.textContent = this.getString("Confirm");
                }
                this.isProcessingLogChanges = false;
                toastr.error(this.getString("Failed to save changes. Please try again."));
                logger.error(logger.Source.ERROR, `Failed to save log changes: ${ e && e.message }`, this.app.userService.getCurrentUser(), e);
            });
    }

    private generateLogCollectionListImpl(displayCollection: IViewLog[]) {
        const logsList = document.getElementById("logs-list-container");
        if (logsList) {
            const listElement = createElement("div", "logs-list");
            generateChecklist({
                el: listElement,
                items: displayCollection,
                labelTemplate: logTemplate,
                isCheckable: false,
                context: this,
                translator: this.translator,
                reverseItems: true
            });
            logsList.innerHTML = "";
            logsList.appendChild(listElement);
        }
    }

    private acceptRequest(e: Event) {
        this.saveRequest(e, true).catch(error => this.handleError(error, true, e));
    }

    private rejectRequest(e: Event) {
        this.saveRequest(e, false).catch(error => this.handleError(error, false, e));
    }

    private toggleDisableRequestButtons(enable: boolean, e: Event, buttonText: string) {
        const acceptButton = document.getElementById("acceptRequest") as HTMLButtonElement;
        const rejectButton = document.getElementById("rejectRequest") as HTMLButtonElement;
        if (acceptButton && rejectButton) {
            acceptButton.disabled = !enable;
            rejectButton.disabled = !enable;
        }

        if (e && e.currentTarget) {
            (e.currentTarget as HTMLElement).textContent = this.getString(buttonText);
        }
    }

    private handleError(error: IApiError, isAccepted: boolean, e: Event) {
        this.isProcessingLogChanges = false;
        logger.error(logger.Source.ERROR, `Failed to save log changes: ${ error && error.message }`, this.app.userService.getCurrentUser(), error);
        toastr.error(this.getString("Failed to save changes. Please try again."));
        this.toggleDisableRequestButtons(false, e, isAccepted ? "Accept" : "Reject");
    }

    private saveRequest(e: Event, isAccepted: boolean): Promise<void> {
        return new Promise(resolve => {
            if (this.isProcessingLogChanges) {
                resolve();
                return;
            }
            this.isProcessingLogChanges = true;

            const editGroups = this.app.dutyStatusLogService.getGroupedExemptionLogEdits(this.exemptionPeriodLogs, undefined, isAccepted);

            // Should get 1 group (the logs on this page)
            const editGroup = editGroups[0];
            if (editGroup && editGroup.editedExemptionLog) {
                this.toggleDisableRequestButtons(false, e, "Saving in progress");

                this.app.dutyStatusLogService.saveRequest(editGroup.editedExemptionLog, isAccepted);
                const promisesToResolve: Promise<void>[] = [this.app.sync.dutyStatusLogs.setItem(editGroup.editedExemptionLog, true, false)];

                if (editGroup.logsToEdit) {
                    editGroup.logsToEdit.forEach(log => {
                        this.app.dutyStatusLogService.saveRequest(log, isAccepted);
                        promisesToResolve.push(this.app.sync.dutyStatusLogs.setItem(log, true, false));
                    });
                }

                if (editGroup.logsToRemove) {
                    editGroup.logsToRemove.forEach(log => {
                        promisesToResolve.push(this.app.sync.dutyStatusLogs.removeItem(log, true));
                    });
                }

                return Promise.all(promisesToResolve)
                    .then(() => this.workflow ? Promise.resolve() : this.app.sync.synchronize(false, null, [SyncType.HOS]))
                    .then(() => {
                        toastr.info(this.getString("Log updated"));
                        this.exitPage();
                        resolve();
                    });
            }

            this.isProcessingLogChanges = false;
            resolve();
            return;
        });
    }

    private renderTemplate(displayCollection: IViewLog[]) {
        return this.display(
            this.templatePage({
                showDrivingNotice: this.isRequestedEdit ? false : this.editableLogSet.size !== this.gapLogsCollection.length,
                isRequestedEdit: this.isRequestedEdit
            })
        )
            .then(() => this.generateLogCollectionListImpl(displayCollection))
            .then(() => this.loadAddresses(displayCollection));
    }

    private loadAddresses(displayCollection: IViewLog[]) {
        if (this.exemptionLog.device.id === "NoDeviceId") {
            return Promise.resolve();
        }

        const logsWithAddress: DutyStatusLog[] = [];
        const logsThatNeedAddress = this.gapLogsCollection.filter(log => !log.location);
        const gpsLookupRecords = logsThatNeedAddress.map(log => {
            return {
                id: log.id,
                dateTime: log.dateTime,
                deviceId: log.device.id
            };
        });

        return getLogRecords(gpsLookupRecords, this.app.api)
            .then(gpsLogs => {
                gpsLogs.forEach((record, index) => {
                    this.gapLogsCollection[index].location = {
                        location: {
                            x: record.length > 0 ? record[0].longitude : 0,
                            y: record.length > 0 ? record[0].latitude : 0
                        }
                    };
                });

                const addressLookupCoordinates: IAddressSearch[] = [];
                this.gapLogsCollection.forEach(log => {
                    if (log.location?.location && log.isValidLocation() && !log._address) {
                        addressLookupCoordinates.push({ id: log.id, coordinate: log.location.location });
                        logsWithAddress.push(log);
                    }
                });

                return populateAddressesRedux(this.app.api, this.app.store, addressLookupCoordinates, false);
            })
            .then((addresses: IAddress[]) => {
                let shouldRerender = false;
                addresses.forEach((address, index) => {
                    const gapLog = logsWithAddress[index];
                    const gapLogAddress = this.formatAddress(address);
                    if (gapLogAddress) {
                        !shouldRerender && (shouldRerender = true);
                        gapLog._address = gapLogAddress;
                        const displayLog = displayCollection.find(log => log.id === gapLog.id);
                        if (displayLog) {
                            displayLog._address = gapLogAddress;
                        }
                    }
                });

                if (shouldRerender) {
                    this.generateLogCollectionListImpl(displayCollection);
                }
            })
            .catch(error => {
                logger.error(logger.Source.UI, `Failed to load addresses: ${ error.message }`, this.app.userService.getCurrentUser(), error);
            });
    }

    private formatAddress(addressObject: IAddress) {
        return addressObject.formattedHosAddress || addressObject.formattedAddress || "";
    }

    private exitPage() {
        if (this.workflow) {
            dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep);
        } else {
            this.app.router.navigate("hos/tabs/logs/logs", true);
        }
    }

    private isEditValid(): boolean {
        if (!this.exemptionAction || !this.exemptionLog || !this.exemptionLog.dateTime) {
            return false;
        }

        const hasSecurityClearance = this.app.securityService.can("editPcYmLogs");

        if (!this.isRequestedEdit && !hasSecurityClearance) {
            return false;
        }

        if (this.isRequestedEdit) {
            if (this.exemptionLog.status !== "PC" && this.exemptionLog.status !== "YM") {
                return false;
            }

            if (this.exemptionAction === EditPcYmAction.Remove && !this.exemptionLog.requestedEdit?.isRequestedRemoval) {
                return false;
            }

            if (this.exemptionAction === EditPcYmAction.Shorten && !this.exemptionPeriodLogs.some(log => log.isSituationalDrivingClear() && log.requestedEdit)) {
                return false;
            }
        } else {
            if (!getExemptionTypeIfValid(this.exemptionPeriodLogs)) {
                return false;
            }

            if (this.exemptionAction === EditPcYmAction.Shorten) {
                if (!this.newSdcDateTime) {
                    return false;
                }

                const nextLog = this.app.dutyStatusLogService.getLastLogInExemptionPeriod(this.exemptionLog);
                if (!nextLog) {
                    return false;
                }

                return !validateExemptionTimeModification(this.newSdcDateTime, this.exemptionLog, nextLog);
            }
        }

        return true;
    }
}